import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import {
  LayoutDashboard,
  FileText,
  Package,
  Image as ImageIcon,
  Languages,
  Settings as SettingsIcon,
  Mail,
  ClipboardList,
  Truck,
  Users,
  ScrollText,
  LogOut,
  Send,
} from "lucide-react";

function NavItem({ to, icon: Icon, label, testid, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground shadow-interactive"
            : "text-tz-cream/80 hover:bg-white/5 hover:text-tz-cream"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      <span className="font-medium tracking-wide">{label}</span>
    </NavLink>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-64 shrink-0 hidden md:flex md:flex-col bg-tz-espresso text-tz-cream sticky top-0 h-screen">
        <div className="px-6 py-7 border-b border-white/5">
          <Link to="/admin" className="block">
            <div className="font-serif text-xl leading-tight">Tambure</div>
            <div className="font-serif text-2xl leading-tight text-primary">Žmegač</div>
            <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-tz-cream/50">
              CMS · v0.2
            </div>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          <NavItem to="/admin" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" end />
          <NavItem to="/admin/pages" icon={FileText} label="Pages" testid="nav-pages" />
          <NavItem to="/admin/products" icon={Package} label="Products" testid="nav-products" />
          <NavItem to="/admin/gallery" icon={ImageIcon} label="Gallery" testid="nav-gallery" />
          <NavItem to="/admin/translations" icon={Languages} label="Translations" testid="nav-translations" />
          <NavItem to="/admin/messages" icon={Mail} label="Messages" testid="nav-messages" />
          <NavItem to="/admin/inquiries" icon={ClipboardList} label="Inquiries" testid="nav-inquiries" />
          <NavItem to="/admin/orders" icon={Truck} label="Orders" testid="nav-orders" />
          <NavItem to="/admin/email-log" icon={Send} label="Email Log" testid="nav-email-log" />
          <NavItem to="/admin/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" />
          <div className="pt-3">
            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] text-tz-cream/40">
              Administration
            </div>
            <NavItem to="/admin/users" icon={Users} label="Users" testid="nav-users" />
            <NavItem to="/admin/audit-log" icon={ScrollText} label="Audit Log" testid="nav-audit" />
          </div>
        </nav>
        <div className="p-4 border-t border-white/5 text-[11px] text-tz-cream/40">
          Ručno izrađene tambure iz Varaždina
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center justify-between px-6 lg:px-10 h-16">
            <div className="md:hidden font-serif text-lg">Tambure Žmegač</div>
            <div className="flex-1" />
            <div className="flex items-center gap-4">
              <a
                href="/"
                target="_blank"
                rel="noreferrer"
                data-testid="admin-view-site-link"
                className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-primary hidden sm:inline"
              >
                View site ↗
              </a>
              <div
                data-testid="admin-header-email"
                className="text-sm text-muted-foreground hidden sm:block"
              >
                {user?.email}
              </div>
              <button
                type="button"
                data-testid="admin-logout-btn"
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.5} />
                Logout
              </button>
            </div>
          </div>
        </header>
        <MaintenanceBanner />
        <main className="flex-1 px-6 lg:px-10 py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
